import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faInstagram,
  faTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { db } from "@lib/firebase";
import { doc, getDoc } from "firebase/firestore";

export const SectionHero = () => {
  const [title, setTitle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const MAX_RETRIES = 3;

  // Fungsi untuk scroll ke section berikutnya
  const scrollToNextSection = () => {
    const nextSection = document.getElementById("date");
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Mengambil data dari Firestore dengan retry logic
  useEffect(() => {
    const fetchEventData = async (attempt = 0) => {
      try {
        if (!db) {
          throw new Error("Firebase belum diinisialisasi");
        }

        const eventRef = doc(db, "semuaKegiatan", "JOTR");
        const eventDoc = await getDoc(eventRef);

        if (eventDoc.exists()) {
          const eventData = eventDoc.data();
          setTitle(eventData.title ? eventData.title.toUpperCase() : null);
          setError(null);
        } else {
          throw new Error("Dokumen Jakarta Open Trail Run tidak ditemukan");
        }
      } catch (err) {
        console.error("Firebase error:", err);

        if (attempt < MAX_RETRIES) {
          setRetryCount(attempt + 1);
          setTimeout(
            () => fetchEventData(attempt + 1),
            Math.pow(2, attempt) * 1000
          );
        } else {
          setError(
            err.message || "Gagal memuat data. Silakan coba lagi nanti."
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, []);

  // Countdown logic
  useEffect(() => {
    const targetDate = new Date("2025-09-27T00:00:00+07:00").getTime();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const timeLeft = targetDate - now;

      if (timeLeft <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  // Reset scroll saat unmount
  useEffect(() => {
    return () => {
      window.scrollTo(0, 0);
    };
  }, []);

  const socialMedia = [
    { icon: faFacebook, link: "#" },
    { icon: faInstagram, link: "https://www.instagram.com/jotr_gepang" },
    { icon: faTwitter, link: "#" },
  ];

  return (
    <section
      id="hero"
      className="w-full -mt-20 pt-20 min-h-screen text-black text-center flex flex-col justify-center relative"
      style={{
        backgroundImage: "url('/bg/bghero11.jpg')",
        backgroundAttachment: "fixed",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-t from-lime-600/100 to-lime-600/20 z-0"></div>

      {/* Desktop: Social media icons (hidden on mobile) */}
      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 hidden md:flex flex-col items-center gap-4">
        {socialMedia.map((social, index) => (
          <motion.a
            key={index}
            href={social.link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white text-xl sm:text-2xl bg-opacity-50 w-8 h-8 flex items-center justify-center rounded-full hover:text-lime-200 transition-colors"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: 0.7 + index * 0.1,
              ease: [0.4, 0, 0.2, 1],
            }}
            viewport={{ once: true }}
          >
            <FontAwesomeIcon icon={social.icon} />
          </motion.a>
        ))}
      </div>
      {/* End Desktop: Social media icons */}

      {/* Konten hanya ditampilkan jika tidak loading */}
      {!loading && (
        <div className="relative text-white text-center px-6 z-1 flex flex-col justify-center flex-grow mt-20 md:pb-28 gap-4">
          {error || !title ? (
            // State error
            <p className="text-white">Gagal memuat data kegiatan.</p>
          ) : (
            // State sukses
            <>
              <motion.img
                src="jotrWhite.svg"
                alt="JOTR Logo"
                className="h-36 md:h-52 mx-auto object-contain"
                initial={{ opacity: 0, y: -15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.2,
                  ease: [0.4, 0, 0.2, 1],
                }}
                viewport={{ once: true }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              <motion.p
                className="text-2xl md:text-5xl lg:text-6xl pt-8 font-bold mx-auto text-white"
                initial={{ opacity: 0, y: -15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.3,
                  ease: [0.4, 0, 0.2, 1],
                }}
                viewport={{ once: true }}
              >
                {title}
              </motion.p>
              <motion.p
                className="text-xl md:text-5xl lg:text-4xl pb-6 font-normal mx-auto text-white"
                initial={{ opacity: 0, y: -15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.5,
                  ease: [0.4, 0, 0.2, 1],
                }}
                viewport={{ once: true }}
              >
                GEDE PANGRANGO SERIES
              </motion.p>
              {/* Mobile and Desktop: Countdown timer */}
              <div className="grid grid-cols-4 gap-2 text-center max-w-sm mx-auto">
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <p className="text-2xl md:text-3xl font-black text-white">
                    {countdown.days}
                  </p>
                  <p className="text-sm md:text-base text-white/80">Days</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <p className="text-2xl md:text-3xl font-black text-white">
                    {countdown.hours}
                  </p>
                  <p className="text-sm md:text-base text-white/80">Hours</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.2, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <p className="text-2xl md:text-3xl font-black text-white">
                    {countdown.minutes}
                  </p>
                  <p className="text-sm md:text-base text-white/80">Minutes</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.3, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <p className="text-2xl md:text-3xl font-black text-white">
                    {countdown.seconds}
                  </p>
                  <p className="text-sm md:text-base text-white/80">Seconds</p>
                </motion.div>
              </div>
              {/* End Mobile and Desktop: Countdown timer */}
              <motion.h2
                className="text-sm sm:text-lg md:text-lg font-normal uppercase text-white"
                initial={{ opacity: 0, y: -15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 1.0,
                  ease: [0.4, 0, 0.2, 1],
                }}
                viewport={{ once: true }}
              >
                #JOTRGEPANG2025
              </motion.h2>
            </>
          )}
        </div>
      )}

      {/* Mobile and Desktop: Scroll button */}
      {!loading && !error && title && (
        <div className="absolute bottom-8 left-0 w-full flex justify-center z-0">
          <motion.div
            className="cursor-pointer text-white animate-bounce z-0"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeInOut" }}
            onClick={scrollToNextSection}
          >
            <FontAwesomeIcon icon={faChevronDown} className="text-4xl" />
          </motion.div>
        </div>
      )}
      {/* End Mobile and Desktop: Scroll button */}
    </section>
  );
};
